import { ChatSendMessageToastContext } from '@/components/chat/ChatSendMessageToast/ChatSendMessageToastProvider'
import { ButtonComponent, DropdownMenu, IconButton } from '@/components/commons'
import { Avatar } from '@/components/commons/images/Avatar.component'
import { useConfirmationModal } from '@/components/confirmationModal/ConfirmationModal'
import {
  RemoveElementAnimation,
  RemoveElementAnimationContext,
} from '@/components/removeElementAnimation/RemoveElementAnimation'
import { IUser } from '@/interfaces/user'
import {
  createConnection,
  declineConnection,
  deleteConnection,
} from '@/services/connections'
import { useProfilePage } from '@/shared/hooks/profilePage'
import { useMutation } from '@/shared/hooks/useMutation'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Icon } from 'public/assets/v2/images/icons'
import React, { createContext, useCallback, useContext } from 'react'
import { BrowserContext, UserContext } from '../../context'
import {
  connectionCardActions,
  connectionCardAvatar,
  connectionCardAvatarContainer,
  connectionCardButton,
  connectionCardButtonIcon,
  connectionCardContainer,
  connectionCardContent,
  connectionCardCreatorType,
  connectionCardInfo,
  connectionCardName,
  connectionCardNameLink,
  connectionCardUsername,
} from './ConnectionCard.css'
import { DisplayNameWithBadge } from '../user/DisplayNameWithBadge'
import { useIsMod, useSubscription } from 'containers/profile/hooks/useProfile'

const ConnectionContext = createContext({} as IUser)

export const ConnectionCard = ({
  connection,
  children,
  className,
  ...rest
}: React.PropsWithChildren<
  { connection: IUser } & React.HTMLProps<HTMLDivElement>
>) => {
  return (
    <ConnectionContext.Provider value={connection}>
      <RemoveElementAnimation>
        <div className={clsx(connectionCardContainer, className)} {...rest}>
          <div className={connectionCardContent}>
            <ConnectionAvatar />
            <div className={connectionCardInfo}>
              <TitleLabel connection={connection} />
              <CreatorTypeLabel />
              {/*<MutualConnectionsLabel />*/}
            </div>
          </div>
          <div className={connectionCardActions}>{children}</div>
        </div>
      </RemoveElementAnimation>
    </ConnectionContext.Provider>
  )
}

const TitleLabel = ({ connection }) => {
  const isMod = useIsMod(connection)
  const { isSubscribed } = useSubscription(connection)

  const { username, creatorType } = useContext(ConnectionContext)

  if (!creatorType || !creatorType.length) {
    return (
      <span className={connectionCardName}>
        <Link
          href={`/${username.toLocaleLowerCase()}`}
          className={connectionCardNameLink}
        >
          <DisplayNameWithBadge profile={connection} />
        </Link>
      </span>
    )
  }

  return (
    <span className={connectionCardName}>
      <Link
        href={`/${username.toLocaleLowerCase()}`}
        className={connectionCardNameLink}
      >
        <DisplayNameWithBadge profile={connection} />
      </Link>{' '}
      {!isMod && !isSubscribed && (
        <span className={connectionCardUsername}>{username}</span>
      )}
    </span>
  )
}

// const MutualConnectionsLabel = () => {
//   const { mutualConnections } = useContext(ConnectionContext)
//
//   if (mutualConnections === undefined) return null
//   if (!mutualConnections) {
//     return (
//       <span className={connectionCardMutualConnectionsLabel}>
//         <Icon.UsersIcon width={14} height={14} /> No Mutual Connections
//       </span>
//     )
//   }
//
//   return (
//     <span className={connectionCardMutualConnectionsLabel}>
//       <Icon.UsersIcon width={14} height={14} /> {mutualConnections} Mutual
//       Connection{mutualConnections > 1 ? 's' : ''}
//     </span>
//   )
// }

const CreatorTypeLabel = () => {
  const { creatorType, username } = useContext(ConnectionContext)

  if (!creatorType || !creatorType.length) {
    return <span className={connectionCardCreatorType}>{username}</span>
  }

  return (
    <span className={connectionCardCreatorType}>{creatorType.join(', ')}</span>
  )
}

const ConnectionAvatar = () => {
  const { avatar, username } = useContext(ConnectionContext)
  return (
    <div className={connectionCardAvatarContainer}>
      <Link href={`/${username.toLocaleLowerCase()}`}>
        <Avatar
          className={connectionCardAvatar}
          size={48}
          image={avatar}
          username={username}
        />
        {/* Commented this code because it will be implemented in the future */}
        {/* <span className={connectionCardAvatarCheckmark}>
          <Icon.CheckmarkIcon className={connectionCardAvatarCheckmarkIcon} />
        </span> */}
      </Link>
    </div>
  )
}

const ConnectionAcceptConnectionRequestButton = ({ onSuccess }) => {
  const { id } = useContext(ConnectionContext)
  const { setUser } = React.useContext(UserContext)
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)

  const [acceptConnection, { isLoading }] = useMutation({
    mutateMethod: createConnection,
  })

  const onClick = useCallback(async () => {
    const result = await acceptConnection(id)
    if (!result) return
    if ('error' in result) {
      // TODO: Treat error
    } else {
      triggerAnimation(() => {
        setUser(result)
        if (onSuccess) onSuccess()
      })
    }
  }, [id, onSuccess, acceptConnection, setUser, triggerAnimation])
  return (
    <ButtonComponent
      loading={isLoading}
      className={connectionCardButton}
      onClick={onClick}
      iconLeft={<Icon.CheckmarkIcon className={connectionCardButtonIcon} />}
      size="xSmall"
      label="Accept"
    />
  )
}

const ConnectionRejectConnectionRequestButton = ({ onSuccess }) => {
  const { id } = useContext(ConnectionContext)
  const { setUser } = React.useContext(UserContext)
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)

  const [rejectConnection, { isLoading }] = useMutation({
    mutateMethod: declineConnection,
  })

  const onClick = useCallback(async () => {
    const result = await rejectConnection(id)
    if (!result) return
    if ('error' in result) {
      // TODO: Treat error
    } else {
      triggerAnimation(() => {
        setUser(result)
        if (onSuccess) onSuccess()
      })
    }
  }, [id, onSuccess, rejectConnection, setUser, triggerAnimation])
  return (
    <ButtonComponent
      loading={isLoading}
      onClick={onClick}
      className={connectionCardButton}
      iconLeft={<Icon.CloseIcon className={connectionCardButtonIcon} />}
      variation="secondary"
      size="xSmall"
      label="Ignore"
    />
  )
}

const ConnectionViewProfileButton = () => {
  const { username } = useContext(ConnectionContext)
  const { isMobile } = useContext(BrowserContext)
  const { ownProfile } = useProfilePage()
  const { push } = useRouter()
  const onClick = useCallback(() => {
    push(`/${username.toLocaleLowerCase()}`)
  }, [username, push])
  if (isMobile && ownProfile) return null
  return (
    <Link href={`/${username.toLocaleLowerCase()}`}>
      <ButtonComponent
        className={connectionCardButton}
        variation="secondary"
        size="xSmall"
        label="Profile"
        onClick={onClick}
      />
    </Link>
  )
}

const ConnectionSendMessageButton = () => {
  const connection = useContext(ConnectionContext)
  const { showToast } = useContext(ChatSendMessageToastContext)
  const { ownProfile } = useProfilePage()
  const onClick = useCallback(() => {
    showToast([connection])
  }, [connection, showToast])
  if (!ownProfile)
    return (
      <ButtonComponent
        className={connectionCardButton}
        size="xSmall"
        label="Message"
        onClick={onClick}
      />
    )
  return (
    <ButtonComponent
      className={connectionCardButton}
      size="xSmall"
      label="Message"
      onClick={onClick}
    />
  )
}

const ConnectionViewMoreButton = ({ onSuccess }) => {
  const { id, username } = useContext(ConnectionContext)
  const { ownProfile } = useProfilePage()
  const { setUser } = React.useContext(UserContext)
  const { triggerAnimation } = useContext(RemoveElementAnimationContext)
  const { isMobile } = useContext(BrowserContext)
  const { push } = useRouter()
  const onClickViewProfile = useCallback(() => {
    push(`/${username.toLocaleLowerCase()}`)
  }, [username, push])
  const [removeConnection] = useMutation({
    mutateMethod: deleteConnection,
  })
  const showConfirmation = useConfirmationModal({
    title: 'Remove Connection',
    message: 'Are you sure that you want to remove this connection?',
  })
  const onClickRemoveConnection = useCallback(async () => {
    const confirmation = await showConfirmation()
    if (!confirmation) return
    const result = await removeConnection(id)
    if (!result) return
    if ('error' in result) {
      // TODO: Treat error
    } else {
      triggerAnimation(() => {
        if (onSuccess) onSuccess()
        setUser(result)
      })
    }
  }, [
    id,
    onSuccess,
    removeConnection,
    setUser,
    showConfirmation,
    triggerAnimation,
  ])
  if (!ownProfile) return null
  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger>
          <IconButton icon="ThreeDotsIcon" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" sideOffset={20} alignOffset={-10}>
          {isMobile ? (
            <DropdownMenu.Item>
              <span onClick={onClickViewProfile}>View Profile</span>
            </DropdownMenu.Item>
          ) : null}
          {ownProfile ? (
            <DropdownMenu.Item>
              <span onClick={onClickRemoveConnection}> Remove Connection</span>
            </DropdownMenu.Item>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu>
    </>
  )
}

ConnectionCard.ViewProfileButton = ConnectionViewProfileButton
ConnectionCard.AcceptConnectionButton = ConnectionAcceptConnectionRequestButton
ConnectionCard.RejectConnectionButton = ConnectionRejectConnectionRequestButton
ConnectionCard.ViewMoreButton = ConnectionViewMoreButton
ConnectionCard.SendMessageButton = ConnectionSendMessageButton
